import * as Yup from "yup";

// ─── Enums ────────────────────────────────────────────────────────────────────
// Single source of truth — import these wherever you need the lists.

export const SERVICE_TYPES = [
  "HOUR",
  "PIECE",
  "LIFT",
  "KM",
  "FLAT",
  "QTY",
  "MATRIX",
] as const;
export const TAX_OPTIONS = ["0", "24", "NO_TAX"] as const;

export type ServiceType = (typeof SERVICE_TYPES)[number];
export type TaxType = (typeof TAX_OPTIONS)[number];

// ─── Service Form Schema ──────────────────────────────────────────────────────
// Validates ONLY the fields registered in react-hook-form.
//
// Matrix fields (zones, bands, priceMatrix) live in local component state
// and are validated separately via validateMatrix() before submission.
// This keeps the Yup schema simple and the resolver conflict-free.

export const createServiceSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required("validation:service.codeRequired")
    .min(2, "validation:service.codeMin")
    .max(30, "validation:service.codeMax"),

  description: Yup.string()
    .trim()
    .max(1000, "validation:service.descMax")
    .nullable()
    .optional(),

  type: Yup.string()
    .oneOf([...SERVICE_TYPES], "validation:service.typeInvalid")
    .required("validation:service.typeRequired"),

  tax: Yup.string()
    .oneOf([...TAX_OPTIONS], "validation:service.taxInvalid")
    .required("validation:service.taxRequired"),

  partnerId: Yup.string().nullable().optional(),

  // The accounting mapping — mandatory on both sides of the directory. A
  // client rate copies the general service's codes and may rewrite them, but
  // both are always filled: a half-mapped service produces an export row the
  // accounting system rejects. Flat fields rather than a nested object so
  // react-hook-form registers them like every other box on the form.
  accountingVatCode: Yup.string()
    .trim()
    .required("validation:service.vatCodeRequired")
    .max(30, "validation:service.accountMax"),

  accountingIncomeAccount: Yup.string()
    .trim()
    .required("validation:service.incomeAccountRequired")
    .max(30, "validation:service.accountMax"),

  // Price: optional for every type, and format-checked only when something was
  // actually typed.
  //
  // An empty box is not a missing answer — it is a service with NO DEFAULT
  // RATE, priced when it is charged. The form sends that as null (see
  // ServiceForm's submit), which is a different stored value from 0: zero is a
  // service that is free. MATRIX keeps its own branch because the field is
  // hidden there entirely, so its value is always "".
  price: Yup.string().when("type", {
    is: "MATRIX",
    then: (s) => s.optional().nullable(),
    otherwise: (s) =>
      s
        .optional()
        .nullable()
        .test(
          "price-format",
          "validation:service.priceFormat",
          (v) => !v || /^\d{1,5}(\.\d{1,2})?$/.test(v),
        ),
  }),
});
